import React, { useState, useCallback, useRef } from 'react';
import { Alert, Button, Col, Input, Modal, Row, Space, Table, Typography } from '../../';
import { InfoCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { useValidateCsvMigrationsMutation, useUploadCsvMigrationsMutation } from '../../../api';
import { useEntitlementValidation } from '../../../hooks';
import { useNotificationContext } from '../../../providers/NotificationProvider';
import { parseCSVToArrays } from '../../../lib/csvUtils';
import './CsvMigrationsUploadModal.scss';

const { Text, Link } = Typography;

const CsvMigrationsUploadModal = ({ open, onClose, onSuccess, userOrg }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [migrations, setMigrations] = useState([]);
    const [migrationMethod, setMigrationMethod] = useState('');
    const [errors, setErrors] = useState([]);
    const [validationErrors, setValidationErrors] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showAllErrors, setShowAllErrors] = useState(false);

    const fileInputRef = useRef(null);
    const { notifySuccess, notifyError } = useNotificationContext();
    const { validateEntitlement, isValidating: isValidatingEntitlement } = useEntitlementValidation();

    // API mutations
    const [validateCsvMigrations] = useValidateCsvMigrationsMutation();
    const [uploadCsvMigrations] = useUploadCsvMigrationsMutation();

    const handleBrowseClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileSelect = useCallback((event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Reset previous state
        setMigrations([]);
        setErrors([]);
        setValidationErrors(null);
        setMigrationMethod('');

        // Check file type
        if (!file.name.toLowerCase().endsWith('.csv')) {
            setErrors(['Please select a CSV file.']);
            setSelectedFile(null);
            setFileName('');
            return;
        }

        setSelectedFile(file);
        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                parseCSVContent(e.target.result);
            } catch (error) {
                setErrors(['Error reading file. Please ensure it is a valid CSV file.']);
                setSelectedFile(null);
                setFileName('');
            }
        };

        reader.readAsText(file);
    }, []);

    const parseCSVContent = useCallback((csvText) => {
        try {
            // Convert all new lines to unix line breaks
            const normalizedCsv = csvText.replace(/(?:\r\n|\r|$)/g, '\n').replace(/\n{2,}/g, '\n');

            const csvArr = parseCSVToArrays(normalizedCsv);

            if (csvArr.length < 2) {
                setErrors(['CSV file must contain at least one data row besides the header.']);
                return;
            }

            // Split header row from data rows
            const headerRow = csvArr[0];
            const dataRows = csvArr.slice(1);

            const parsedMigrations = [];
            const parseErrors = [];

            // Process each data row
            for (let idx = 0; idx < dataRows.length; idx++) {
                const row = dataRows[idx];

                // Skip completely empty rows
                if (row.every((cell) => !cell || !cell.trim())) {
                    continue;
                }

                try {
                    const item = {};

                    for (let x = 0; x < row.length; x++) {
                        if (!headerRow[x]) continue;

                        const paramLabel = headerRow[x]
                            .toLowerCase()
                            .replace(/ /g, '_')
                            .replace(/_\(.*\)/g, '');

                        // Handle array fields (same as legacy logic)
                        const arrayFields = [
                            'disks',
                            'partitions',
                            'security_group_names',
                            'disk_datastores',
                            'disks_provisioning_type',
                            'instance_tags',
                            'instance_labels',
                            'network_tags',
                            'storage_policies',
                            'vm_tags',
                            'resize_partition_size',
                            'ip_type',
                            'ip_address',
                            'assignment_type',
                            'netmask',
                            'destination_network_name',
                            'discovered_sql_server_instances',
                            'sql_modernization',
                            'transfer_method',
                            'disk_type',
                            'security_group_ids',
                            'datastore',
                            'ip_pool',
                            'volume_types',
                            'storage_path',
                            'virtual_lan_identification',
                            'primary_internal_ephemeral_ip_address',
                            'subnet_name',
                            'custom_ephemeral_ip_address',
                            'external_ip',
                            'network_tier',
                            'network_adapter_type',
                            'subnet_id',
                            'auto-assign_public_ip',
                            'static_private_ip',
                            'destination_network_project_id',
                        ];

                        if (arrayFields.includes(paramLabel)) {
                            item[paramLabel] = row[x].trim() !== '' ? row[x].split(',').map((val) => val.trim()) : [];
                        } else {
                            item[paramLabel] = row[x];
                        }

                        // Get migration method from first row
                        if (idx === 0 && paramLabel === 'migration_method') {
                            setMigrationMethod(row[x].toLowerCase());
                        }
                    }

                    parsedMigrations.push(item);
                } catch (error) {
                    parseErrors.push(`Row ${idx + 2}: Error parsing row - ${error.message}`);
                }
            }

            if (parseErrors.length > 0) {
                setErrors(parseErrors);
            } else {
                setErrors([]);
            }

            setMigrations(parsedMigrations);
        } catch (error) {
            console.error('CSV parsing error:', error);
            setErrors(['One or more errors parsing the CSV file.']);
            setMigrations([]);
        }
    }, []);

    const handleSubmit = useCallback(async () => {
        if (!selectedFile || migrations.length === 0) {
            notifyError({ description: 'Please upload a valid CSV file with migration data.' });
            return;
        }

        if (validationErrors) {
            notifyError({
                description: 'CSV Migrations has validation errors. Please fix the errors and upload again.',
            });
            return;
        }

        try {
            setLoading(true);

            // First validate entitlement
            const entitlement = await validateEntitlement(userOrg);

            // Prepare payload
            const payload = {
                entitlement_id: entitlement.id,
                csv_migration_data: migrations,
                migration_method: migrationMethod || undefined,
            };

            // Validate CSV migrations
            notifySuccess({ description: 'Migrations validated successfully, start processing.' });
            await validateCsvMigrations(payload).unwrap();

            // Upload CSV migrations
            const result = await uploadCsvMigrations(payload).unwrap();

            notifySuccess({ description: 'Migrations processed successfully. Starting preflights.' });

            // Close modal and trigger success callback
            handleCancel();
            if (onSuccess) {
                onSuccess({ migrationProfileID: result.id });
            }
        } catch (error) {
            console.error('Error uploading CSV migrations:', error);

            if (error?.data) {
                setValidationErrors(error.data);
                setShowAllErrors(true);

                if (error.data.validation_errors) {
                    notifyError({
                        message: 'CSV Migrations Validation Error',
                        description: 'Migrations CSV has validation errors. Please fix the errors and upload again.',
                    });
                } else if (error.data.message) {
                    notifyError({ description: error.data.message });
                } else {
                    notifyError({ description: 'CSV Migrations Processing Error occurred. Please try again.' });
                }
            } else if (error.message?.includes('entitlement')) {
                notifyError({
                    description:
                        'No valid entitlements were found. Please contact your RiverMeadow Administrator or Support.',
                });
            } else {
                notifyError({ description: 'Failed to upload CSV migrations. Please try again.' });
            }
        } finally {
            setLoading(false);
        }
    }, [
        selectedFile,
        migrations,
        validationErrors,
        userOrg,
        migrationMethod,
        validateEntitlement,
        validateCsvMigrations,
        uploadCsvMigrations,
        notifySuccess,
        notifyError,
        onSuccess,
    ]);

    const handleCancel = useCallback(() => {
        // Reset all form state
        setSelectedFile(null);
        setFileName('');
        setMigrations([]);
        setMigrationMethod('');
        setErrors([]);
        setValidationErrors(null);
        setShowAllErrors(false);
        setLoading(false);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        onClose();
    }, [onClose]);

    const toggleShowAllErrors = useCallback(() => {
        setShowAllErrors(!showAllErrors);
    }, [showAllErrors]);

    // Validation errors table columns
    const errorColumns = [
        {
            title: 'Source IP/Source VM Name',
            dataIndex: 'source_ip',
            key: 'source_ip',
            width: '40%',
        },
        {
            title: 'Validation Errors',
            dataIndex: 'errors',
            key: 'errors',
            render: (errors) => (
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {errors?.map((error, index) => (
                        <li key={index} style={{ color: '#ff4d4f' }}>
                            {error.message}
                        </li>
                    ))}
                </ul>
            ),
        },
    ];

    const isFormValid = selectedFile && migrations.length > 0 && !validationErrors;

    return (
        <Modal
            title="Upload Migrations CSV"
            open={open}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel} disabled={loading}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loading}
                    onClick={handleSubmit}
                    disabled={!isFormValid}
                    icon={<UploadOutlined />}
                >
                    {loading ? 'Uploading Migrations CSV...' : 'Upload'}
                </Button>,
            ]}
            width={600}
            className="csv-migrations-upload-modal"
        >
            <Space orientation="vertical" style={{ width: '100%' }} size="middle">
                {/* Help Link */}
                <Row justify="end">
                    <Col>
                        <Link
                            href="https://docs.rivermeadow.com/bulk-migrations"
                            target="_blank"
                            style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                        >
                            <InfoCircleOutlined style={{ fontSize: 14, color: '#1890ff' }} />
                            How to run Migrations from a CSV
                        </Link>
                    </Col>
                </Row>

                {/* File Upload */}
                <Space.Compact style={{ width: '100%' }}>
                    <Button onClick={handleBrowseClick} disabled={loading}>
                        Browse
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />
                    <Input
                        value={fileName}
                        placeholder="No file selected"
                        readOnly
                        style={{ flex: 1 }}
                        className="file-name-input"
                    />
                </Space.Compact>

                {/* Parse Errors */}
                {errors.length > 0 && (
                    <Alert
                        type="error"
                        title="CSV Parsing Errors"
                        description={
                            <ul style={{ margin: 0, paddingLeft: 16 }}>
                                {errors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        }
                    />
                )}

                {/* Validation Errors */}
                {validationErrors && (
                    <Space orientation="vertical" style={{ width: '100%' }}>
                        {validationErrors.validation_errors && validationErrors.validation_errors.length > 0 && (
                            <>
                                <Alert
                                    type="error"
                                    title={`Validation Errors (${validationErrors.validation_errors.length})`}
                                    description="The following migrations have validation errors and need to be corrected:"
                                    showIcon
                                    style={{ marginBottom: 8 }}
                                />
                                <Table
                                    dataSource={
                                        showAllErrors
                                            ? validationErrors.validation_errors
                                            : validationErrors.validation_errors.slice(0, 2)
                                    }
                                    columns={errorColumns}
                                    pagination={false}
                                    size="small"
                                    bordered
                                    rowKey={(record, index) => index}
                                    className="validation-errors-table"
                                />
                                {validationErrors.validation_errors.length > 2 && (
                                    <Button type="link" onClick={toggleShowAllErrors}>
                                        {showAllErrors ? 'Show Less Errors' : 'Show All Errors'}
                                    </Button>
                                )}
                            </>
                        )}
                        {validationErrors.message && (
                            <Alert type="error" title="Error" description={validationErrors.message} showIcon />
                        )}
                    </Space>
                )}

                {/* Success message for parsed migrations */}
                {migrations.length > 0 && !errors.length && !validationErrors && (
                    <Alert
                        title="CSV Parsed Successfully"
                        description={`Found ${migrations.length} migration(s) in the CSV file. Click Upload to proceed.`}
                        type="success"
                        showIcon
                    />
                )}

                {/* Loading state */}
                {(loading || isValidatingEntitlement) && (
                    <Alert
                        title="Processing"
                        description={
                            isValidatingEntitlement ? 'Validating entitlements...' : 'Uploading migrations CSV...'
                        }
                        type="info"
                        showIcon
                    />
                )}
            </Space>
        </Modal>
    );
};

export default CsvMigrationsUploadModal;
